
import Vue from 'vue';
import { message } from 'ant-design-vue';
import { getEducationList, getWorkDetail, saveApi, updateApi } from '../api';
import AiSupplementFields from '../components-pc/AiSupplementFields.vue';
import HomeFooter from '../components-pc/HomeFooter.vue';
import PcPageLoading from '../components-pc/PcPageLoading.vue';
import WorkFileUploader from '../components-pc/WorkFileUploader.vue';

const baseUrl = 'cqcc';

type AudioTab = 'long' | 'poster' | 'h5';

interface AudioForm {
  workName: string;
  authorName: string;
  teacherName: string;
  schoolName: string;
  grade: string;
  education: string;
  contact: string;
  intro: string;
  resultLink: string;
  ai: AiSupplementForm;
}

interface AiSupplementForm {
  isAi: string;
  tools: string;
  cover: string;
  overUrl: string;
  contents: string;
}

interface UploaderRef extends Vue {
  hasFiles(): boolean;
  uploadToOss(): Promise<string[]>;
  setInitialFile(url: string): void;
}

interface AiSupplementInstance extends Vue {
  validate(): boolean;
  uploadCoverToOss(): Promise<string>;
  clear(): void;
  setInitialCover(url: string): void;
}

interface EducationOption {
  label: string;
  value: string;
}

interface UploadRefConfig {
  key: string;
  ref: string;
  label: string;
  paramKey: string;
  required?: boolean;
}

const createEmptyAudioForm = (): AudioForm => ({
  workName: '',
  authorName: '',
  teacherName: '',
  schoolName: '',
  grade: '',
  education: '',
  contact: '',
  intro: '',
  resultLink: '',
  ai: {
    isAi: '1',
    tools: '',
    cover: '',
    overUrl: '',
    contents: '',
  },
});

export default Vue.extend({
  name: 'AudioSignupPc',
  components: {
    AiSupplementFields,
    HomeFooter,
    PcPageLoading,
    WorkFileUploader,
  },
  data() {
    return {
      activeTab: 'long' as AudioTab,
      pageLoading: false,
      submitting: false,
      aiCoverValidating: false,
      uploaderResetKey: 0,
      educationOptions: [] as EducationOption[],
      tabList: [
        { key: 'long', title: '长图作品' },
        { key: 'poster', title: '海报作品' },
        { key: 'h5', title: 'H5作品' },
      ] as Array<{ key: AudioTab; title: string }>,
      uploaderValidating: {} as { [key: string]: boolean },
      posterSizeRule: {
        jpg: 10,
        jpeg: 10,
        png: 10,
        gif: 20,
        mp4: 100,
      },
      form: createEmptyAudioForm(),
    };
  },
  mounted() {
    this.initPageData();
  },
  computed: {
    hasValidatingUploader(): boolean {
      return this.getActiveUploadConfigs().some(
        (item: UploadRefConfig) => this.uploaderValidating[item.key],
      );
    },
  },
  methods: {
    async initPageData() {
      this.pageLoading = true;
      try {
        await Promise.all([
          this.fetchEducationOptions(),
          this.fetchWorkDetail(),
        ]);
      } finally {
        this.pageLoading = false;
      }
    },
    handleTabChange(tab: AudioTab) {
      if (this.activeTab === tab || this.submitting) {
        return;
      }
      this.resetForm();
      this.activeTab = tab;
    },
    resetForm() {
      this.form = createEmptyAudioForm();
      this.uploaderValidating = {};
      this.aiCoverValidating = false;
      this.uploaderResetKey += 1;
      const aiSupplement = this.$refs.aiSupplement as AiSupplementInstance;
      if (aiSupplement) {
        aiSupplement.clear();
      }
    },
    goSignup() {
      this.$router.push(`/${baseUrl}/signup`).catch((err: Error) => err);
    },
    getEditId(): string {
      const id = this.$route.query.id;
      return Array.isArray(id) ? String(id[0] || '') : String(id || '');
    },
    getDetailPayload(res: any): any {
      return (
        (res && res.data && res.data.data) || (res && res.data) || res || {}
      );
    },
    normalizeAiFlag(value: any): string {
      return Number(value) === 1 ? '1' : '0';
    },
    getDetailAiFlag(detail: any): any {
      if (
        detail &&
        detail.is_ai !== undefined &&
        detail.is_ai !== null &&
        detail.is_ai !== ''
      ) {
        return detail.is_ai;
      }
      return detail && detail.isAi;
    },
    createAiSupplementForm(detail?: any): AiSupplementForm {
      const rawFlag = detail ? this.getDetailAiFlag(detail) : '1';
      return {
        isAi:
          rawFlag === undefined || rawFlag === null || rawFlag === ''
            ? '1'
            : this.normalizeAiFlag(rawFlag),
        tools: (detail && detail.tools) || '',
        cover: (detail && detail.cover) || '',
        overUrl: (detail && (detail.over_url || detail.overUrl)) || '',
        contents: (detail && detail.contents) || '',
      };
    },
    getAiSubmitParams(coverUrl: string) {
      const aiForm = this.form.ai;
      const isAi = aiForm.isAi === '1';
      return {
        is_ai: isAi ? 1 : 0,
        tools: isAi ? aiForm.tools : '',
        cover: isAi ? coverUrl : '',
        over_url: isAi ? aiForm.overUrl : '',
        contents: isAi ? aiForm.contents : '',
      };
    },
    parseUrlList(value: any): string[] {
      if (Array.isArray(value)) {
        return value.filter(Boolean).map((item) => String(item));
      }
      if (!value) {
        return [];
      }
      try {
        const list = JSON.parse(value);
        return Array.isArray(list)
          ? list.filter(Boolean).map((item) => String(item))
          : [];
      } catch (err) {
        return [String(value)];
      }
    },
    getAudioTabByType(audioType: string | number): AudioTab {
      if (Number(audioType) === 2) {
        return 'poster';
      }
      if (Number(audioType) === 3) {
        return 'h5';
      }
      return 'long';
    },
    async fetchWorkDetail() {
      const id = this.getEditId();
      if (!id) {
        return;
      }

      try {
        const res: any = await getWorkDetail({ id });
        const detail = this.getDetailPayload(res);
        this.activeTab = this.getAudioTabByType(
          detail.audioType || detail.audio_type || 1,
        );
        this.form = {
          workName: detail.title || '',
          authorName: detail.name || '',
          teacherName: detail.teacher || '',
          schoolName: detail.school || '',
          grade: detail.class || '',
          education: detail.education || '',
          contact:
            detail.contact ||
            detail.contact_way ||
            detail.contactWay ||
            detail.phone ||
            detail.mobile ||
            '',
          intro: detail.introduction || '',
          resultLink: detail.exp_url || '',
          ai: this.createAiSupplementForm(detail),
        };
        this.$nextTick(() => {
          this.getActiveUploadConfigs().forEach((item: UploadRefConfig) => {
            const uploader = this.getUploader(item.ref);
            if (!uploader) {
              return;
            }
            const urls =
              item.paramKey === 'files'
                ? this.parseUrlList(detail.files)
                : item.paramKey === 'material'
                ? this.parseUrlList(detail.material)
                : this.parseUrlList(detail.explanation);
            uploader.setInitialFile(urls[0] || '');
          });
          const aiSupplement = this
            .$refs.aiSupplement as AiSupplementInstance;
          if (aiSupplement) {
            aiSupplement.setInitialCover(this.form.ai.cover);
          }
        });
      } catch (err) {
        console.error('视听设计作品详情获取失败：', err);
        message.error('作品详情获取失败，请稍后重试');
      }
    },
    setUploaderValidating(key: string, validating: boolean) {
      this.$set(this.uploaderValidating, key, validating);
    },
    getActiveUploadConfigs(): UploadRefConfig[] {
      if (this.activeTab === 'long') {
        return [
          {
            key: 'longDesc',
            ref: 'longDescUploader',
            label: '作品说明',
            paramKey: 'explanation',
          },
          {
            key: 'longWork',
            ref: 'longWorkUploader',
            label: '上传作品',
            paramKey: 'files',
          },
        ];
      }
      if (this.activeTab === 'poster') {
        return [
          {
            key: 'posterWork',
            ref: 'posterWorkUploader',
            label: '上传作品',
            paramKey: 'files',
          },
          {
            key: 'posterSource',
            ref: 'posterSourceUploader',
            label: 'PSD/AI等源文件',
            paramKey: 'explanation',
          },
        ];
      }
      return [
        {
          key: 'h5Qr',
          ref: 'h5QrUploader',
          label: '作品二维码',
          paramKey: 'files',
        },
        {
          key: 'h5Source',
          ref: 'h5SourceUploader',
          label: 'PSD/AI等源文件',
          paramKey: 'explanation',
          required: false,
        },
        {
          key: 'h5Material',
          ref: 'h5MaterialUploader',
          label: '录屏素材',
          paramKey: 'material',
          required: false,
        },
      ];
    },
    normalizeEducationOption(item: any): EducationOption | null {
      if (typeof item === 'string' || typeof item === 'number') {
        const value = String(item);
        return { label: value, value };
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      const label =
        item.label ||
        item.name ||
        item.title ||
        item.education ||
        item.text ||
        item.value;
      const value = item.value || item.id || item.key || label;

      if (!label || value === undefined || value === null) {
        return null;
      }

      return {
        label: String(label),
        value: String(value),
      };
    },
    getEducationPayload(res: any): any[] {
      if (Array.isArray(res)) {
        return res;
      }

      if (Array.isArray(res && res.data)) {
        return res.data;
      }

      if (Array.isArray(res && res.list)) {
        return res.list;
      }

      if (Array.isArray(res && res.data && res.data.list)) {
        return res.data.list;
      }

      return [];
    },
    async fetchEducationOptions() {
      try {
        const res: any = await getEducationList();
        const options = this.getEducationPayload(res)
          .map((item) => this.normalizeEducationOption(item))
          .filter(Boolean) as EducationOption[];

        this.educationOptions = options;
      } catch (err) {
        console.error('学历列表获取失败：', err);
        message.error('学历列表获取失败，请稍后重试');
      }
    },
    getAudioType(): number {
      if (this.activeTab === 'long') {
        return 1;
      }
      if (this.activeTab === 'poster') {
        return 2;
      }
      return 3;
    },
    getUploader(refName: string): UploaderRef | null {
      const ref = this.$refs[refName] as
        | UploaderRef
        | UploaderRef[]
        | undefined;
      return Array.isArray(ref) ? ref[0] : ref || null;
    },
    isValidLink(link: string): boolean {
      return /^https?:\/\/.+/i.test(link);
    },
    isSaveSuccess(res: any): boolean {
      return Boolean(
        res &&
          (res.code === 200 ||
            res.status === 200 ||
            res.status === 1 ||
            res.success === true),
      );
    },
    getResponseMessage(res: any, fallback: string): string {
      return (res && (res.msg || res.message)) || fallback;
    },
    validateForm(): boolean {
      const requiredFields: Array<{ key: keyof AudioForm; label: string }> = [
        { key: 'workName', label: '作品名称' },
        { key: 'authorName', label: '作者名字' },
        { key: 'teacherName', label: '指导教师' },
        { key: 'schoolName', label: '所属院校' },
        { key: 'grade', label: '年级' },
        { key: 'education', label: '学历' },
        { key: 'contact', label: '联系方式' },
      ];
      if (this.activeTab !== 'long') {
        requiredFields.push({ key: 'intro', label: '作品简介' });
      }
      const emptyField = requiredFields.find((item) => !this.form[item.key]);

      if (emptyField) {
        message.error(`请填写${emptyField.label}`);
        return false;
      }

      if (this.activeTab === 'h5' && !this.form.resultLink) {
        message.error('请填写作品链接');
        return false;
      }
      if (this.form.resultLink && !this.isValidLink(this.form.resultLink)) {
        message.error('请输入正确的作品链接');
        return false;
      }

      const missingUploader = this.getActiveUploadConfigs()
        .filter((item) => item.required !== false)
        .find((item) => {
          const uploader = this.getUploader(item.ref);
          return !uploader || !uploader.hasFiles();
        });
      if (missingUploader) {
        message.error(`请上传${missingUploader.label}`);
        return false;
      }

      const aiSupplement = this.$refs.aiSupplement as AiSupplementInstance;
      if (aiSupplement && !aiSupplement.validate()) {
        return false;
      }

      return true;
    },
    async submitForm() {
      if (
        this.submitting ||
        this.hasValidatingUploader ||
        this.aiCoverValidating ||
        !this.validateForm()
      ) {
        return;
      }

      const uploadConfigs = this.getActiveUploadConfigs();
      const aiSupplement = this.$refs.aiSupplement as AiSupplementInstance;
      const hideLoading = message.loading('作品上传中，请稍候...', 0);
      this.submitting = true;

      try {
        const [uploadResults, aiCoverUrl] = await Promise.all([
          Promise.all(
            uploadConfigs.map(async (item) => {
              const uploader = this.getUploader(item.ref);
              const urls = uploader ? await uploader.uploadToOss() : [];
              return {
                key: item.paramKey,
                urls,
              };
            }),
          ),
          aiSupplement ? aiSupplement.uploadCoverToOss() : Promise.resolve(''),
        ]);
        const fileParams = uploadResults.reduce(
          (result: { [key: string]: string[] }, item) => {
            result[item.key] = item.urls;
            return result;
          },
          {},
        );
        const submitParams = {
          workType: 2,
          audioType: this.getAudioType(),
          title: this.form.workName,
          name: this.form.authorName,
          school: this.form.schoolName,
          introduction: this.activeTab === 'long' ? '' : this.form.intro,
          files: JSON.stringify(fileParams.files || []),
          teacher: this.form.teacherName,
          class: this.form.grade,
          education: this.form.education,
          contact: this.form.contact,
          explanation: (fileParams.explanation || [])[0] || '',
          material: (fileParams.material || [])[0] || '',
          exp_url: this.form.resultLink,
          ...this.getAiSubmitParams(aiCoverUrl),
        };

        const id = this.getEditId();
        const res: any = id
          ? await updateApi({ ...submitParams, id })
          : await saveApi(submitParams);
        if (!this.isSaveSuccess(res)) {
          message.error(this.getResponseMessage(res, '提交失败，请稍后重试'));
          return;
        }

        message.success(
          this.getResponseMessage(res, id ? '修改成功' : '提交成功'),
        );
        this.resetForm();
        this.$router.back();
      } catch (err) {
        console.error('视听设计作品提交失败：', err);
        message.error('提交失败，请稍后重试');
      } finally {
        hideLoading();
        this.submitting = false;
      }
    },
  },
});
